import React, { useState, useEffect } from 'react';
import {
    UserCheck, Plus, Phone, Mail, FileText,
    Send, CheckCircle, Clock, Filter, Search,
    ChevronRight, IndianRupee, Briefcase
} from 'lucide-react';
import api from '../api/axiosInstance';

const CRMModule = () => {
    const [leads, setLeads] = useState([
        { id: 1, name: "Rahul Mehta", phone: "98200XXXXX", status: "New", source: "Instagram", budget: "5-8L" },
        { id: 2, name: "Sneha Kapoor", phone: "91670XXXXX", status: "Quoted", source: "Referral", budget: "12L+" }
    ]);
    const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);

    const getStatusColor = (status) => {
        switch (status) {
            case 'New': return 'bg-blue-50 text-blue-600 border-blue-100';
            case 'Quoted': return 'bg-amber-50 text-amber-600 border-amber-100';
            case 'Converted': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
            default: return 'bg-slate-50 text-slate-600 border-slate-100';
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter">Sales Pipeline</h1>
                    <p className="text-slate-500 font-medium italic">Convert inquiries into profitable projects.</p>
                </div>
                <button
                    onClick={() => setIsLeadModalOpen(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-2xl font-bold shadow-xl shadow-indigo-100 transition-all flex items-center gap-2"
                >
                    <Plus size={18} /> New Inquiry
                </button>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">

                {/* LEADS LIST */}
                <div className="xl:col-span-2 space-y-4">
                    <div className="bg-white rounded-[40px] border border-slate-100 p-8 shadow-sm">
                        <div className="flex justify-between items-center mb-8">
                            <h3 className="text-xl font-black text-slate-900">Active Inquiries</h3>
                            <div className="flex gap-2">
                                <Search size={18} className="text-slate-300" />
                                <Filter size={18} className="text-slate-300" />
                            </div>
                        </div>

                        <div className="space-y-3">
                            {leads.map(lead => (
                                <div
                                    key={lead.id}
                                    onClick={() => setSelectedLead(lead)}
                                    className={`flex items-center justify-between p-6 rounded-[32px] border transition-all cursor-pointer group ${selectedLead?.id === lead.id ? 'border-indigo-600 bg-indigo-50/30' : 'border-slate-50 bg-slate-50/50 hover:bg-white hover:border-slate-200'
                                        }`}
                                >
                                    <div className="flex items-center gap-5">
                                        <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center text-indigo-600 font-bold">
                                            {lead.name[0]}
                                        </div>
                                        <div>
                                            <p className="font-bold text-slate-900">{lead.name}</p>
                                            <div className="flex items-center gap-3 mt-1">
                                                <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{lead.source}</span>
                                                <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                                                <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{lead.budget}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-4">
                                        <span className={`px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border ${getStatusColor(lead.status)}`}>
                                            {lead.status}
                                        </span>
                                        <ChevronRight size={18} className="text-slate-300 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* QUICK QUOTATION PREVIEW */}
                <div className="space-y-6">
                    {selectedLead ? (
                        <div className="bg-slate-900 rounded-[40px] p-8 text-white shadow-2xl sticky top-8">
                            <div className="flex items-center gap-3 mb-8">
                                <div className="p-3 bg-indigo-500 rounded-2xl shadow-lg shadow-indigo-500/20">
                                    <FileText size={24} />
                                </div>
                                <h3 className="text-xl font-black italic">Quotation Engine</h3>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Client Prospect</p>
                                    <p className="text-xl font-bold">{selectedLead.name}</p>
                                </div>

                                <div className="p-6 bg-white/5 rounded-3xl border border-white/10 space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-slate-400">Consultation Fee</span>
                                        <span className="font-bold text-sm">₹5,000</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-slate-400">Estimated Project</span>
                                        <span className="font-bold text-sm">₹{selectedLead.budget}</span>
                                    </div>
                                    <div className="pt-4 border-t border-white/10 flex justify-between items-end">
                                        <span className="text-[10px] font-black uppercase text-indigo-400">Potential Revenue</span>
                                        <span className="text-2xl font-black">₹{selectedLead.budget}</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <button className="bg-white text-slate-900 font-bold py-4 rounded-2xl text-xs hover:bg-indigo-50 transition-all flex items-center justify-center gap-2">
                                        <Send size={14} /> Send Quote
                                    </button>
                                    <button className="bg-white/10 text-white font-bold py-4 rounded-2xl text-xs hover:bg-white/20 transition-all flex items-center justify-center gap-2">
                                        <Briefcase size={14} /> Create Project
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="h-full min-h-[400px] border-4 border-dashed border-slate-100 rounded-[40px] flex flex-col items-center justify-center p-12 text-center text-slate-300">
                            <UserCheck size={48} className="mb-4 opacity-20" />
                            <p className="font-bold italic">Select a lead to initiate the <br /> quotation workflow.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CRMModule;